import { useMemo, useState, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { aggregateByDimension, type StatDimension } from '../../lib/session-stats.js';
import { formatDurationMs } from '../../lib/format.js';

interface TimeStackedBarProps {
  spans: ReadonlyArray<TimelineSpan>;
}

const KIND_COLORS: Record<string, string> = {
  llm: 'var(--k-llm)',
  tts: 'var(--k-tts)',
  stt: 'var(--k-stt)',
  pronunciation: 'var(--k-pronunciation)',
  voice: 'var(--k-voice)',
  tool_call: 'var(--k-tool_call)',
  retrieval: 'var(--k-retrieval)',
  guardrail: 'var(--k-guardrail)',
  agent_decision: 'var(--k-agent_decision)',
  http: 'var(--k-http)',
  db_query: 'var(--k-db_query)',
  pipeline_stage: 'var(--k-pipeline_stage)',
  module_step: 'var(--k-module_step)',
  job: 'var(--k-job)',
};

const PROVIDER_COLORS = [
  'oklch(65% 0.20 290)',
  'oklch(70% 0.18 50)',
  'oklch(70% 0.15 195)',
  'oklch(72% 0.14 340)',
  'oklch(60% 0.16 140)',
  'oklch(65% 0.14 220)',
];

function colorFor(dim: StatDimension, key: string, index: number): string {
  if (dim === 'kind') return KIND_COLORS[key] ?? 'var(--text-tertiary)';
  return PROVIDER_COLORS[index % PROVIDER_COLORS.length]!;
}

/**
 * Stacked horizontal bar: distribución de tiempo (sum durationMs) por kind
 * o provider, ordenado por contribución descendente.
 */
export function TimeStackedBar({ spans }: TimeStackedBarProps): ReactElement {
  const [dim, setDim] = useState<StatDimension>('kind');
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const buckets = useMemo(() => aggregateByDimension(spans, dim), [spans, dim]);
  const total = useMemo(
    () => buckets.reduce((acc, b) => acc + b.durationMs, 0),
    [buckets],
  );

  if (total === 0 || buckets.length === 0) {
    return (
      <div className="sess-chart-card" data-testid="time-stacked-empty">
        <div className="sess-chart-head">
          <span className="sess-chart-title">Tiempo</span>
          <DimToggle dim={dim} onChange={setDim} />
        </div>
        <div className="empty" style={{ height: 120 }}>
          Sin spans con duración registrada.
        </div>
      </div>
    );
  }

  return (
    <div className="sess-chart-card" data-testid="time-stacked">
      <div className="sess-chart-head">
        <span className="sess-chart-title">Tiempo</span>
        <span className="sess-chart-total mono">{formatDurationMs(total)}</span>
        <DimToggle dim={dim} onChange={setDim} />
      </div>
      <div className="sess-stack-body">
        <div className="sess-stack-bar" role="img" aria-label="Distribución de tiempo">
          {buckets.map((b, i) => {
            const pct = (b.durationMs / total) * 100;
            const dimmed = hoverKey !== null && hoverKey !== b.key;
            return (
              <div
                key={b.key}
                className={`sess-stack-seg${dimmed ? ' is-dim' : ''}`}
                style={{ width: `${pct}%`, background: colorFor(dim, b.key, i) }}
                onMouseEnter={() => setHoverKey(b.key)}
                onMouseLeave={() => setHoverKey(null)}
                title={`${b.key}: ${formatDurationMs(b.durationMs)} (${pct.toFixed(1)}%)`}
                data-testid={`time-seg-${b.key}`}
              />
            );
          })}
        </div>
        <ul className="sess-chart-legend sess-chart-legend-row">
          {buckets.map((b, i) => {
            const pct = (b.durationMs / total) * 100;
            const dimmed = hoverKey !== null && hoverKey !== b.key;
            return (
              <li
                key={b.key}
                className={`sess-legend-row${dimmed ? ' is-dim' : ''}`}
                onMouseEnter={() => setHoverKey(b.key)}
                onMouseLeave={() => setHoverKey(null)}
                data-testid={`time-legend-${b.key}`}
              >
                <span
                  className="sess-legend-swatch"
                  style={{ background: colorFor(dim, b.key, i) }}
                />
                <span className="sess-legend-key mono">{b.key}</span>
                <span className="sess-legend-val mono">{formatDurationMs(b.durationMs)}</span>
                <span className="sess-legend-pct mono">{pct.toFixed(1)}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

interface DimToggleProps {
  dim: StatDimension;
  onChange: (next: StatDimension) => void;
}

function DimToggle({ dim, onChange }: DimToggleProps): ReactElement {
  return (
    <div className="seg-wf" role="group" aria-label="Dimensión">
      <button
        type="button"
        className={dim === 'kind' ? 'is-on' : ''}
        onClick={() => onChange('kind')}
        data-testid="time-dim-kind"
      >
        kind
      </button>
      <button
        type="button"
        className={dim === 'provider' ? 'is-on' : ''}
        onClick={() => onChange('provider')}
        data-testid="time-dim-provider"
      >
        provider
      </button>
    </div>
  );
}
